import { useEffect, memo, useState, useRef } from 'react';
import DragDrawer from '@/components/drag-drawer';
import DrawerToast from './components/drawer-toast';
import WarningModal from './components/warning-modal';
import QuestionDetailDrawer from './components/question-detail-drawer';
import SimilarSearchDrawer from './components/similar-search-drawer';
import QaInitOverlay from './components/qa-init-overlay';
import NavgationBar from './components/navigation-bar';
import DialogueArea from './components/dialogue-area';
import DialogueTool from './components/dialogue-tool';
import CombinePicturesDrawer from './components/combine-pictures-drawer';
import KnowledgePointsDrawer from './components/knowledge-points-drawer';
import { getGlobalStore } from '@/store/global';
import { getQaStore } from '@/store/qa';
import { recoverBodyScroll, stopBodyScroll } from '@/utils';
import { IMicroAppProps, INoticeData } from '@/types';
import './index.scss';

const Qa = ({ onReceiveMessageFromMicroApp, registryNoticeMicroAppFn }: IMicroAppProps) => {
    const { publish, subscribe, setExternalCallbacks, externalCallbacksRef } = getGlobalStore()!;
    const [open, setOpen] = useState(false);
    const paramsRef = useRef<Record<string, any>>({});

    const handleCloseDrawer = () => {
        setOpen(false);
        // 因为弹窗关闭是有动效的，所以这里延迟清空数据，防止用户看到清空后的白屏
        setTimeout(reset, 500);
        externalCallbacksRef.current.onCloseDrawer?.(getQaStore()?.qaConfig);
    };

    const reset = () => {
        paramsRef.current = {};
        publish('NavgationBar', { type: 'reset' });
        publish('DialogueArea', { type: 'reset' });
        publish('QaInitOverlay', { type: 'reset' });
        getQaStore()?.clearQaStore();
    };

    useEffect(() => {
        if (onReceiveMessageFromMicroApp) {
            subscribe('main-app', onReceiveMessageFromMicroApp);
            publish('main-app', { type: 'success', content: '通信已建立' });
        }
        if (registryNoticeMicroAppFn) {
            registryNoticeMicroAppFn(({ type, params = {}, callbacks = {} }: INoticeData) => {
                switch (type) {
                    case 'open':
                        console.log('答疑组件接收到的初始化参数：', params, callbacks);
                        paramsRef.current = params;
                        setExternalCallbacks(callbacks);
                        setOpen(true);
                        break;
                    case 'open_rights_modal':
                        publish('RightsModal', { type: 'open', params });
                        break;
                    default:
                        handleCloseDrawer();
                        break;
                }
            });
        }
        subscribe('QaDrawer', ({ type, params = {}, callbacks = {} }: INoticeData) => {
            switch (type) {
                case 'open':
                    paramsRef.current = params;
                    setExternalCallbacks(callbacks);
                    setOpen(true);
                    break;
                default:
                    handleCloseDrawer();
                    break;
            }
        });
        return reset;
    }, []);

    useEffect(() => {
        if (!open) {
            recoverBodyScroll();
            return;
        }
        stopBodyScroll();
        const {
            lucaAskChatId,
            askId,
            lucaId,
            projectId,
            subjectId,
            twoLevelSubjectId,
            entrySource,
            courseType,
            courseId,
            sceneSource,
            csItemId,
            resourceId,
            resourceType,
            showCreateNewQaBtn = true,
            scense,
            cropUrl,
            allowChangeDeterminedIds = false,
        } = paramsRef.current;
        const defaultIds = [projectId, subjectId, twoLevelSubjectId].filter(item => !!item);
        getQaStore()?.updateInitConfig(
            {
                // init参数作为初始化答疑组件的数据，点击新建问题时需使用本参数
                initResourceType: resourceType,
                initResourceId: resourceId,
                initCourseId: courseId,
                initProjectId: projectId,
                initSubjectId: subjectId,
                initTwoLevelSubjectId: twoLevelSubjectId,
                resourceType,
                resourceId,
                courseType,
                courseId,
                sourceMark: 'gaodun',
                clientSource: 'gaodun-pc',
                entrySource,
                showCreateNewQaBtn,
                sceneSource,
                csItemId,
                lucaId,
                // 非学习空间场景下，外部传入的项目、一级科目、二级科目不可反选到哪一级
                disabledInitReverseSelectLevel: [undefined, 0, 8].includes(sceneSource) ? undefined : defaultIds.length,
            },
            true
        );
        // 优先级 lucaAskChatId（“新的”历史答疑）>  askId（“老的”历史答疑）> lucaId（创建“新的”答疑）
        if (lucaAskChatId) {
            publish('QaInitOverlay', { type: 'initByLucaAskChatId', params: { lucaAskChatId } });
        } else if (askId) {
            publish('QaInitOverlay', { type: 'initByAskId', params: { askId } });
        } else if (lucaId) {
            scense === 'home_ocr'
                ? getGlobalStore()?.publish('CombinePicturesDrawer', { type: 'crop_success', url: cropUrl, lucaId, defaultIds })
                : publish('QaInitOverlay', { type: 'initByLucaId', params: { lucaId, defaultIds, allowChangeDeterminedIds } });
        }
    }, [open]);

    return (
        <DragDrawer zIndex={1100} width={375} open={open} onClose={handleCloseDrawer}>
            <div className="qa">
                <NavgationBar />
                <DialogueArea />
                <DialogueTool />
                <SimilarSearchDrawer />
                <QuestionDetailDrawer />
                <CombinePicturesDrawer />
                <KnowledgePointsDrawer />
                <QaInitOverlay />
                {/* 这里可能和详情弹窗的DrawerToast的通信会产生冲突，所以使用判断临时处理一下 */}
                {open && <DrawerToast />}
                <WarningModal />
            </div>
        </DragDrawer>
    );
};

export default memo(Qa);
